import { ClientOnly, createFileRoute, redirect } from "@tanstack/react-router";
import { AFISHA_CITIES, afishaCityBySlug, type AfishaCity } from "@/lib/afisha-cities";
import {
  afishaCardDateLabel,
  afishaCountPhrase,
  afishaDateLabel,
  afishaDayLabel,
  afishaPriceLabel,
  buildAfishaJsonLd,
  eventsCountLabel,
  isTodayEvent,
  istanbulTodayLabel,
  ongoingEvents,
  shouldIndexAfisha,
  upcomingEvents,
  weekendEvents,
} from "@/lib/afisha-page";
import { getAfishaCityEvents } from "@/lib/afisha.functions";
import { mapPublicEventRow } from "@/lib/public-events";
import { EventsScreen } from "@/routes/events";

const SITE = "https://turkey-drive.com";
const OG_IMAGE = `${SITE}/assets/social-preview.jpg`;
const MAX_CARDS = 30;

type AfishaEvent = ReturnType<typeof mapPublicEventRow>;

export const Route = createFileRoute("/afisha/$city")({
  loader: async ({ params }) => {
    const city = afishaCityBySlug(params.city);
    if (!city) throw redirect({ to: "/events" });
    const events = await getAfishaCityEvents({ data: city.city });
    return { city, events };
  },
  head: ({ params, loaderData }) => {
    const url = `${SITE}/afisha/${params.city}`;
    if (!loaderData) {
      return {
        meta: [{ title: "Афиша — TurkeyDrive" }, { name: "robots", content: "noindex" }],
        links: [{ rel: "canonical", href: url }],
      };
    }
    const { city, events } = loaderData;
    const n = events.length;
    const title = `Афиша ${city.genitive}: события на ближайшие дни | TurkeyDrive`;
    const description = `Куда сходить ${city.prepositional}: ${eventsCountLabel(n)} впереди, ближайшие даты, цены и ссылки. Афиша на русском языке.`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        ...(shouldIndexAfisha(n) ? [] : [{ name: "robots", content: "noindex,follow" }]),
        { property: "og:type", content: "website" },
        { property: "og:locale", content: "ru_RU" },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:image", content: OG_IMAGE },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: OG_IMAGE },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(buildAfishaJsonLd(events, city, SITE)),
        },
      ],
    };
  },
  component: AfishaCityPage,
  errorComponent: ({ error }) => (
    <div role="alert" className="p-6 text-center text-sm text-td-muted">
      {error instanceof Error ? error.message : String(error)}
    </div>
  ),
});

function EventCard({ e, now }: { e: AfishaEvent; now: Date }) {
  const price = afishaPriceLabel(e.price);
  return (
    <a
      href={`/e/${e.id}`}
      className="flex min-w-0 items-center gap-3 rounded-2xl border border-td-line bg-white p-3 transition hover:border-td-teal"
    >
      {e.cover_image_url && (
        <img
          decoding="async"
          src={e.cover_image_url}
          alt={e.title}
          loading="lazy"
          className="h-16 w-16 shrink-0 rounded-xl object-cover"
        />
      )}
      <div className="min-w-0 flex-1">
        <p className="break-words text-[15px] font-semibold leading-snug text-td-dark">{e.title}</p>
        <p className="mt-0.5 text-[13px] text-td-muted">
          {afishaCardDateLabel(e, now)} · {e.city}
          {price ? ` · ${price}` : ""}
        </p>
      </div>
    </a>
  );
}

function EventSection({
  id,
  title,
  events,
  now,
}: {
  id: string;
  title: string;
  events: AfishaEvent[];
  now: Date;
}) {
  if (events.length === 0) return null;
  return (
    <section id={id} className="mt-6 scroll-mt-4">
      <h2 className="font-brand text-lg font-bold text-td-dark">{title}</h2>
      <div className="mt-3 flex flex-col gap-2">
        {events.map((e) => (
          <EventCard key={e.id} e={e} now={now} />
        ))}
      </div>
    </section>
  );
}

/** Серверный HTML для поисковиков, ИИ-ботов и людей без JS. На клиенте заменяется настоящей афишей. */
function ServerList({ city, events }: { city: AfishaCity; events: AfishaEvent[] }) {
  const now = new Date();
  const upcoming = upcomingEvents(events, now);
  const ongoing = ongoingEvents(events, now);
  const today = upcoming.filter((e) => isTodayEvent(e.event_date, now));
  const weekend = weekendEvents(events, now);
  const kids = upcoming.filter((e) => e.audience_tag === "family");
  const nearest = upcoming[0] ?? null;
  const current = ongoing[0] ?? null;

  return (
    <main className="min-h-screen overflow-x-hidden bg-white pb-10">
      <div className="mx-auto w-full max-w-[640px] px-4 pt-5">
        <a href="/" className="inline-flex min-h-11 items-center text-[13px] text-td-muted hover:text-td-dark">
          ← TurkeyDrive
        </a>

        <h1 className="mt-4 font-brand text-2xl font-bold leading-tight text-td-dark">
          Афиша {city.genitive}: события на ближайшие дни
        </h1>
        <p className="mt-1 text-[13px] text-td-muted">Обновлено: {istanbulTodayLabel(now)}</p>
        <p className="mt-2 text-[15px] leading-snug text-td-dark">
          {upcoming.length === 0
            ? `Пока предстоящих событий ${city.prepositional} нет — загляни в общую афишу.`
            : `Впереди ${eventsCountLabel(upcoming.length)} ${city.prepositional}: ближайшее «${nearest!.title}» ${afishaDateLabel(nearest!.event_date)}.`}
          {current
            ? ` Сейчас идёт: «${current.title}» (до ${afishaDayLabel(current.event_end_date ?? null)}).`
            : ""}
        </p>

        {(today.length > 0 || weekend.length > 0 || kids.length > 0) && (
          <nav aria-label="Быстрые переходы" className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-[14px] font-semibold text-td-teal">
            {today.length > 0 && <a href="#today" className="min-h-11 py-2">Сегодня</a>}
            {weekend.length > 0 && <a href="#weekend" className="min-h-11 py-2">На выходных</a>}
            {kids.length > 0 && <a href="#kids" className="min-h-11 py-2">Дети</a>}
          </nav>
        )}

        <EventSection id="today" title="Сегодня" events={today} now={now} />
        <EventSection id="weekend" title="На выходных" events={weekend} now={now} />
        <EventSection id="kids" title="Дети" events={kids} now={now} />

        <section className="mt-6">
          <h2 className="font-brand text-lg font-bold text-td-dark">Все события {city.genitive}</h2>
          <div className="mt-3 flex flex-col gap-2">
            {events.slice(0, MAX_CARDS).map((e) => (
              <EventCard key={e.id} e={e} now={now} />
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}

function AfishaCityPage() {
  const { city, events } = Route.useLoaderData();
  const now = new Date();
  const upcoming = upcomingEvents(events, now);
  const weekend = weekendEvents(events, now);
  const nearest = upcoming[0] ?? null;
  const last = upcoming[upcoming.length - 1] ?? null;

  return (
    <>
      <ClientOnly fallback={<ServerList city={city} events={events} />}>
        <EventsScreen initialCity={city.city} title={`Афиша ${city.genitive}`} />
      </ClientOnly>

      <section className="mx-auto w-full max-w-[640px] px-4 pb-10">
        <h2 className="font-brand text-lg font-bold text-td-dark">О афише {city.genitive}</h2>
        <p className="mt-2 text-[14px] leading-relaxed text-td-muted">
          TurkeyDrive собирает события русскоязычного сообщества {city.prepositional}: встречи,
          мастер-классы, вечеринки и семейные активности. Отметь «Я иду», чтобы видеть, кто ещё будет.
        </p>

        <div className="mt-6 flex flex-col gap-5">
          <div>
            <h3 className="text-[15px] font-semibold text-td-dark">Куда сходить {city.prepositional} на выходных?</h3>
            {weekend.length === 0 ? (
              <p className="mt-1 text-[14px] text-td-muted">Пока событий на выходные нет — загляни в афишу.</p>
            ) : (
              <ul className="mt-1 list-disc pl-5 text-[14px] text-td-muted">
                {weekend.slice(0, 3).map((e) => (
                  <li key={e.id}>
                    <a href={`/e/${e.id}`} className="font-semibold text-td-teal hover:underline">{e.title}</a>
                    {" — "}{afishaDayLabel(e.event_date)}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <h3 className="text-[15px] font-semibold text-td-dark">Сколько событий в афише {city.genitive}?</h3>
            <p className="mt-1 text-[14px] text-td-muted">
              {upcoming.length === 0
                ? "Сейчас предстоящих событий нет."
                : afishaCountPhrase(upcoming.length, nearest!.event_date, last!.event_date)}
            </p>
          </div>
          <div>
            <h3 className="text-[15px] font-semibold text-td-dark">Как отметить, что ты идёшь?</h3>
            <p className="mt-1 text-[14px] text-td-muted">
              Зарегистрируйся в TurkeyDrive и нажми «Я иду» на странице события.
            </p>
          </div>
        </div>

        <a href="/events" className="mt-6 inline-flex min-h-11 items-center text-[15px] font-semibold text-td-teal">
          Вся афиша →
        </a>
        <p className="mt-1 text-[14px] text-td-muted">
          Афиша по городам:{" "}
          {AFISHA_CITIES.map((c, i) => (
            <span key={c.slug}>
              {i > 0 && " · "}
              {c.slug === city.slug ? (
                c.name
              ) : (
                <a href={`/afisha/${c.slug}`} className="font-semibold text-td-teal hover:underline">
                  {c.name}
                </a>
              )}
            </span>
          ))}
        </p>
      </section>
    </>
  );
}
